import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux'
import { RefreshCw, ExternalLink, AlertTriangle, Delete } from 'lucide-react'
import { formatCurrency } from '@/helpers/billing'
import { formatDateSafe, formatDateTimeSafe, getDateRange } from '@/pages/Dashboard/helpers/dateRange'
import { syncDashboardSubscriptions, getAdminTransactions, syncAdminTransactions, getAdminMrr } from '@/services/adminDashboardService'
import { LinkCustomerModal } from '@/pages/Dashboard/components/LinkCustomerModal'
import { adminDashboardActions } from '@/redux/slice/adminDashboardSlice'
import { DateRange } from '@/constants/filters/filters'
import { Pagination } from '@/components/custom/Pagination/Pagination'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ButtonVariant } from '@/constants/button/button'
import { INPUT_FIELD_CLASSNAME } from '@/constants/classname/classname'

// ── Style constants ───────────────────────────────────────────────────────────

const cardClass = 'bg-card border border-border rounded-md overflow-hidden'
const cardHeaderClass = 'flex items-start justify-between p-4'
const cardTitleClass = 'text-sm font-semibold text-card-foreground leading-snug'
const cardSubtitleClass = 'text-xs text-secondary mt-1'
const thClass = 'text-left px-4 py-[9px] text-[11px] font-semibold uppercase tracking-[0.05em] text-secondary border-b border-border whitespace-nowrap'
const tdClass = 'text-left px-4 py-[11px] border-b border-slate-100 dark:border-slate-800 align-middle text-[13px] text-card-foreground'

const DEFAULT_SUBSCRIPTIONS_PAGE_SIZE = 10
const DEFAULT_TRANSACTIONS_PAGE_SIZE = 10
const DEFAULT_TRANSACTIONS_FETCH_LIMIT = 5000

// ── Badge helper ──────────────────────────────────────────────────────────────

const InlineBadge = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap${className ? ` ${className}` : ''}`}>
    {children}
  </span>
)

function statusBadgeClass(status: string): string {
  const map: Record<string, string> = {
    paid:          'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    active:        'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    open:          'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200',
    failed:        'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200',
    uncollectible: 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200',
    draft:         'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    void:          'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  }
  return map[status] ?? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ── Component ─────────────────────────────────────────────────────────────────

export const BillingHub = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { loading } = useSelector((state: RootState) => state.app)
  const { token } = useSelector((state: RootState) => state.user)
  const { mrr, transactions, transactionsFilterDateRange } = useSelector((state: RootState) => state.adminDashboard)
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkModalCustomerId, setLinkModalCustomerId] = useState("")
  const [linkModalCustomerEmail, setLinkModalCustomerEmail] = useState("")

  const [subscriptionsSearchQuery, setSubscriptionsSearchQuery] = useState('')
  const [subscriptionsPaginationState, setSubscriptionsPaginationState] = useState({
    limit: DEFAULT_SUBSCRIPTIONS_PAGE_SIZE,
    offset: 0
  })

  const [transactionsPaginationState, setTransactionsPaginationState] = useState({
    limit: DEFAULT_TRANSACTIONS_PAGE_SIZE,
    offset: 0
  })

  const handleSyncSubscriptions = async () => {
    if(!token) return
    await dispatch(syncDashboardSubscriptions(token))
    await dispatch(getAdminMrr(token))
  }

  const handleSyncTransactions = async () => {
    if(!token) return
    const { startDate, endDate } = getDateRange(transactionsFilterDateRange)
    await dispatch(syncAdminTransactions(token, { startDate, endDate }))
    await dispatch(getAdminTransactions(token, {
      startDate: getDateRange(transactionsFilterDateRange).startDate,
      endDate: getDateRange(transactionsFilterDateRange).endDate,
      limit: DEFAULT_TRANSACTIONS_FETCH_LIMIT,
      offset: 0
    }, true))
    setTransactionsPaginationState((prev) => ({ ...prev, offset: 0 }))
  }

  const updateTransactionsTable = async (range: DateRange) => { 
    if (!token) return
    const { startDate, endDate } = getDateRange(range)
    await dispatch(getAdminTransactions(token, {
      startDate,
      endDate,
      limit: DEFAULT_TRANSACTIONS_FETCH_LIMIT,
      offset: 0
    }))
    setTransactionsPaginationState((prev) => ({ ...prev, offset: 0 }))
  }

  const handleOpenLinkModal = (customerId: string, email?: string) => {
    setLinkModalCustomerId(customerId)
    setLinkModalCustomerEmail(email ?? "")
    setLinkModalOpen(true)
  }

  const handleLinkCustomerSuccess = async () => {
    setLinkModalOpen(false)
    if (token) { 
      await dispatch(getAdminMrr(token))
      const { startDate, endDate } = getDateRange(transactionsFilterDateRange)
      await dispatch(getAdminTransactions(token, {
        startDate,
        endDate,
        limit: DEFAULT_TRANSACTIONS_FETCH_LIMIT,
        offset: 0
      }))
      setTransactionsPaginationState((prev) => ({ ...prev, offset: 0 }))
    }
  }

  const subscriptions = useMemo(() => mrr?.subscriptions ?? [], [mrr?.subscriptions])

  const filteredSubscriptions = useMemo(() => {
    const normalizedQuery = subscriptionsSearchQuery.trim().toLowerCase()
    if (!normalizedQuery) return subscriptions

    return subscriptions.filter((sub) => (sub.organizationName ?? '').toLowerCase().includes(normalizedQuery))
  }, [subscriptions, subscriptionsSearchQuery])

  useEffect(() => {
    setSubscriptionsPaginationState((prev) => {
      const total = filteredSubscriptions.length
      if (total === 0) {
        return prev.offset === 0 ? prev : { ...prev, offset: 0 }
      }

      const lastPageOffset = Math.floor((total - 1) / prev.limit) * prev.limit
      if (prev.offset > lastPageOffset) {
        return { ...prev, offset: lastPageOffset }
      }

      return prev
    })
  }, [filteredSubscriptions.length, subscriptionsPaginationState.limit])

  const paginatedSubscriptions = useMemo(() => {
    const start = subscriptionsPaginationState.offset
    const end = start + subscriptionsPaginationState.limit
    return filteredSubscriptions.slice(start, end)
  }, [filteredSubscriptions, subscriptionsPaginationState.limit, subscriptionsPaginationState.offset])

  const unlinkedCount = subscriptions.filter((s) => !s.isLinked).length

  const filteredTransactions = useMemo(() => transactions?.items ?? [], [transactions?.items])

  useEffect(() => {
    setTransactionsPaginationState((prev) => {
      const total = filteredTransactions.length
      if (total === 0) {
        return prev.offset === 0 ? prev : { ...prev, offset: 0 }
      }

      const lastPageOffset = Math.floor((total - 1) / prev.limit) * prev.limit
      if (prev.offset > lastPageOffset) {
        return { ...prev, offset: lastPageOffset }
      }

      return prev
    })
  }, [filteredTransactions.length, transactionsPaginationState.limit])

  const paginatedTransactions = useMemo(() => {
    const start = transactionsPaginationState.offset
    const end = start + transactionsPaginationState.limit
    return filteredTransactions.slice(start, end)
  }, [filteredTransactions, transactionsPaginationState.limit, transactionsPaginationState.offset])

  return token && (
    <div>
      {/* ── MRR & Subscriptions Card ─────────────────────────────────────── */}
      <div className={`${cardClass} mb-5`}>
        {/* Header */}
        <div className={cardHeaderClass}>
          <div className='flex flex-col items-start'>
            <div className={cardTitleClass}>Monthly Recurring Revenue</div>
            <div className={cardSubtitleClass}>Active subscriptions · matches Stripe</div>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleSyncSubscriptions}
            disabled={loading}
          >
            <RefreshCw size={14} className='mr-2' />Sync subscriptions
          </Button>
        </div>

        {/* MRR Hero + Alert */}
        <div className="p-4">
          <div className="text-4xl font-bold leading-none tracking-tight text-card-foreground">
            {new Intl.NumberFormat('en-US', {
              style: 'currency', currency: 'USD',
              minimumFractionDigits: 0, maximumFractionDigits: 0,
            }).format((mrr?.totalMrrCents ?? 0) / 100)}
            <span className="ml-1.5 text-lg font-medium text-secondary">/mo</span>
          </div>
          {mrr && (
            <p className="mt-1.5 text-xs text-secondary">
              <span className="mr-3 inline-flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-blue-600" />
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format((mrr.breakdown.playbookBasedCents ?? 0) / 100)} playbook-based
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format((mrr.breakdown.planTierBasedCents ?? 0) / 100)} plan-tier (legacy)
              </span>
            </p>
          )}

          {unlinkedCount > 0 && (
            <div className="flex items-center gap-2.5 mt-3 rounded-md border border-amber-200 px-3.5 py-2.5 text-xs bg-amber-50 text-amber-800">
              <AlertTriangle size={16} className="text-amber-500 shrink-0" />
              <span>
                <strong>{unlinkedCount} subscription{unlinkedCount !== 1 ? "s are" : " is"} not linked to a TCS organization.</strong>
                {" "}Associate it below to resolve Stripe webhook errors.
              </span>
            </div>
          )}

          <div className="mt-4 flex w-full items-center gap-2 md:max-w-md relative">
            <Input
              value={subscriptionsSearchQuery}
              onChange={(event) => {
                setSubscriptionsSearchQuery(event.target.value)
                setSubscriptionsPaginationState((prev) => ({ ...prev, offset: 0 }))
              }}
              placeholder="Filter subscriptions by organization name"
              className={`${INPUT_FIELD_CLASSNAME} bg-foreground text-primary`}
              aria-label="Filter subscriptions by organization name"
            />
            {subscriptionsSearchQuery && (
              <Button
                type="button"
                variant={ButtonVariant.ICON}
                className="h-9 text-destructive rounded rounded-md bg-destructive-foreground absolute right-[2px]"
                onClick={() => setSubscriptionsSearchQuery("")}
                disabled={!subscriptionsSearchQuery.trim()}
              >
                <Delete size={16} />
              </Button>
            )}
          </div>
        </div>

        {/* Subscriptions Table */}
        <div className="border-t border-border overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                <th className={thClass}>Organization / Customer</th>
                <th className={thClass}>Plan</th>
                <th className={thClass}>Amount</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Renews</th>
                <th className={thClass} style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {paginatedSubscriptions.map((sub) => (
                <tr
                  key={sub.stripeSubscriptionId}
                  className={sub.isLinked ? 'hover:bg-slate-50 dark:hover:bg-slate-900' : 'bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100 dark:hover:bg-amber-950/30'}
                >
                  <td className={tdClass}>
                    {sub.isLinked ? (
                      <>
                        <div className="font-medium text-card-foreground">{sub.organizationName || sub.stripeCustomerId || '—'}</div>
                        {sub.stripeCustomerId && (
                          <div className="text-[11px] text-slate-400 mt-px">{sub.stripeCustomerId}</div>
                        )}
                      </>
                    ) : (
                      <>
                        <InlineBadge className="bg-amber-50 text-amber-800 border border-amber-200 mb-1">
                          <AlertTriangle size={10} />
                          Not linked to TCS org
                        </InlineBadge>
                        {sub.customerEmail && (
                          <div className="text-[11px] text-slate-400 mt-0.5">{sub.customerEmail}</div>
                        )}
                        {sub.stripeCustomerId && (
                          <div className="text-[11px] text-slate-400 mt-px">{sub.stripeCustomerId}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td className={tdClass}>
                    {sub.pricingModel === "playbook_based_v2" ? (
                      <InlineBadge className="bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                        Playbook{sub.playbookCount ? ` ×${sub.playbookCount}` : ""}
                      </InlineBadge>
                    ) : sub.pricingModel === "plan_tier_based_v1" && sub.planTier ? (
                      <InlineBadge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                        {sub.planTier} (legacy)
                      </InlineBadge>
                    ) : (
                      <InlineBadge className="bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500">Unknown</InlineBadge>
                    )}
                  </td>
                  <td className={`${tdClass} font-medium tabular-nums`}>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format((sub.amountCents ?? 0) / 100)}
                    {sub.interval === 'year'
                      ? '/yr'
                      : sub.interval === 'month' || !sub.interval
                        ? '/mo'
                        : `/${sub.interval}`}
                  </td>
                  <td className={tdClass}>
                    <InlineBadge className={statusBadgeClass(sub.status)}>
                      {sub.status === 'active' && <span className="text-[8px]">●</span>}
                      {capitalize(sub.status)}
                    </InlineBadge>
                  </td>
                  <td className={`${tdClass} whitespace-nowrap text-secondary`}>
                    {formatDateSafe(sub.currentPeriodEnd)}
                  </td>
                  <td className={`${tdClass} whitespace-nowrap`}>
                    <div className="flex justify-end items-center gap-1.5">
                      {!sub.isLinked && (
                        <button
                          type="button"
                          onClick={() => handleOpenLinkModal(sub.stripeCustomerId, sub.customerEmail ?? undefined)}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-medium cursor-pointer bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200"
                        >
                          Link to org
                        </button>
                      )}
                      <a
                        href={sub.stripeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-[3px] text-blue-600 text-[12px] no-underline px-[6px] py-[3px] rounded hover:bg-blue-50"
                      >
                        Stripe <ExternalLink size={11} />
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 pb-5">
          <Pagination
            total={filteredSubscriptions.length}
            limit={subscriptionsPaginationState.limit}
            offset={subscriptionsPaginationState.offset}
            onPageChange={(newOffset) => setSubscriptionsPaginationState((prev) => ({ ...prev, offset: newOffset }))}
            onLimitChange={(newLimit) => setSubscriptionsPaginationState({ limit: newLimit, offset: 0 })}
          />
        </div>
      </div>

      {/* ── Transactions Card ─────────────────────────────────────────────── */}
      <div className={cardClass}>
        {/* Header */}
        <div className={cardHeaderClass}>
          <div className='flex flex-col items-start'>
            <div className={cardTitleClass}>Transactions</div>
            <div className={cardSubtitleClass}>Invoices and charges · sourced from Stripe</div>
          </div>
          <div className="flex items-center gap-2.5 flex-shrink-0">
            {/* Date range pills */}
            <div className="flex gap-1">
              {(Object.values(DateRange)).map((range) => (
                <button
                  type="button"
                  key={range}
                  onClick={() => { 
                    dispatch(adminDashboardActions.setDateRange(range))
                    updateTransactionsTable(range)
                  }}
                  className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer border ${
                    range === transactionsFilterDateRange
                      ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-border bg-card text-secondary hover:border-slate-300'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleSyncTransactions}
              disabled={loading}
            >
              <RefreshCw size={14} className='mr-2' />Sync with Stripe
            </Button>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="border-t border-border overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                <th className={thClass}>Date</th>
                <th className={thClass}>Customer</th>
                <th className={thClass}>Organization</th>
                <th className={thClass}>Amount</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Payment Method</th>
                <th className={thClass}>Type</th>
                <th className={thClass} style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.map((item) => (
                <tr
                  key={item.id}
                  className={item.isLinked ? 'hover:bg-slate-50 dark:hover:bg-slate-900' : 'bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100 dark:hover:bg-amber-950/30'}
                >
                  <td className={`${tdClass} whitespace-nowrap text-secondary`}>
                    {formatDateTimeSafe(item.paidAt ?? item.createdAt)}
                  </td>
                  <td className={tdClass}>
                    <div className="font-medium text-card-foreground">
                      {item.customerEmail ?? item.customerName ?? "—"}
                    </div>
                    {item.stripeCustomerId && (
                      <div className="text-[11px] text-secondary mt-px">{item.stripeCustomerId}</div>
                    )}
                  </td>
                  <td className={tdClass}>
                    {item.isLinked ? (
                      <span className="text-card-foreground">{item.organizationName ?? "—"}</span>
                    ) : (
                      <InlineBadge className="bg-amber-50 text-amber-800 border border-amber-200">
                        <AlertTriangle size={10} />
                        Unlinked
                      </InlineBadge>
                    )}
                  </td>
                  <td className={`${tdClass} font-medium tabular-nums`}>
                    {formatCurrency((item.amountPaidCents ?? 0) / 100)}
                  </td>
                  <td className={tdClass}>
                    <InlineBadge className={statusBadgeClass(item.status)}>
                      {capitalize(item.status)}
                    </InlineBadge>
                  </td>
                  <td className={tdClass}>
                    {item.paymentMethodBrand && item.paymentMethodLast4 ? (
                      // Card: e.g. VISA ·· 1111
                      <div className="flex items-center gap-[5px] text-xs">
                        <span className="bg-slate-100 border border-border rounded-sm px-[5px] py-px text-[10px] font-semibold text-slate-600 uppercase dark:bg-slate-800 dark:text-slate-400">
                          {item.paymentMethodBrand}
                        </span>
                        <span className="text-secondary">·· {item.paymentMethodLast4}</span>
                      </div>
                    ) : item.paymentMethodBrand ? (
                      // Link / other wallet: just show the type label
                      <span className="bg-green-50 border border-green-200 rounded-sm px-1.5 py-px text-[10px] font-semibold text-green-800 capitalize dark:bg-green-950 dark:border-green-800 dark:text-green-200">
                        {item.paymentMethodBrand}
                      </span>
                    ) : <span className="text-secondary">—</span>}
                  </td>
                  <td className={tdClass}>
                    {item.type === "subscription" ? (
                      item.stripeSubscriptionId ? (
                        <a
                          href={`https://dashboard.stripe.com/subscriptions/${item.stripeSubscriptionId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 text-[11px] no-underline hover:underline"
                        >
                          Subscription ↗
                        </a>
                      ) : (
                          <span className="text-[11px] text-secondary">Subscription</span>
                      )
                    ) : (
                      <span className="text-[11px] text-secondary">
                        One-time{item.description ? ` · ${item.description}` : ''}
                      </span>
                    )}
                  </td>
                  <td className={`${tdClass} whitespace-nowrap`}>
                    <div className="flex justify-end items-center gap-1.5">
                      {!item.isLinked && (
                        <button
                          type="button"
                          onClick={() => handleOpenLinkModal(item.stripeCustomerId, item.customerEmail ?? undefined)}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-medium cursor-pointer bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200"
                        >
                          Link
                        </button>
                      )}
                      <a
                        href={item.stripeInvoiceUrl ?? `https://dashboard.stripe.com/invoices/${item.stripeInvoiceId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-[3px] text-blue-600 text-[12px] no-underline px-[6px] py-[3px] rounded hover:bg-blue-50"
                        title="View in Stripe"
                      >
                        Stripe <ExternalLink size={11} />
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {transactions && (
          <div className="px-6 pb-5">
            <Pagination
              total={filteredTransactions.length}
              limit={transactionsPaginationState.limit}
              offset={transactionsPaginationState.offset}
              onPageChange={(newOffset) => setTransactionsPaginationState((prev) => ({ ...prev, offset: newOffset }))}
              onLimitChange={(newLimit) => setTransactionsPaginationState({ limit: newLimit, offset: 0 })}
              pageSizeOptions={[DEFAULT_TRANSACTIONS_PAGE_SIZE]}
            />
          </div>
        )}
      </div>

      {/* Link Customer Modal */}
      <LinkCustomerModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        stripeCustomerId={linkModalCustomerId}
        customerEmail={linkModalCustomerEmail}
        token={token}
        onSuccess={handleLinkCustomerSuccess}
      />
    </div>
  )
}
